import React, { useState, useEffect } from 'react';
import { Counter } from './Counter';
import { RadioGroup } from './RadioGroup';
import { useDataContext } from '../../context/data';
import translations from '../../constants/translations.json';
import { compact } from 'lodash';

export const MultiCounter = ({
  items = [],
  onChange,
  defaultValues = {},
  currency = 'DKK',
  maxOptionCount = Infinity,
  attribute = {},
  totalSelectedCount = 0, // Total count across all attributes
  selectedValues = null,
  connectedAttributes = [],
  onConnectedAttributeChange,
  selectedAttributes = [],
}) => {
  const { language } = useDataContext();

  // Initialize counts from selectedValues (for editing) or defaultValues
  const getInitialCounts = () => {
    // If editing an existing item with extras
    if (selectedValues) {
      // Convert selectedValues array to object format
      return selectedValues.reduce((acc, extra) => {
        acc[extra.id] = extra.quantity || 0;
        return acc;
      }, {});
    }

    // Default initialization
    return items.reduce((acc, item) => {
      acc[item.id] = defaultValues[item.id] || item.initialCount || 0;
      return acc;
    }, {});
  };

  const [counts, setCounts] = useState(getInitialCounts());

  // Update counts when selectedValues changes (when editing different items)
  useEffect(() => {
    if (selectedValues) {
      const newCounts = selectedValues.reduce((acc, extra) => {
        acc[extra.id] = extra.quantity || 0;
        return acc;
      }, {});

      // Initialize all other items to 0
      items.forEach((item) => {
        if (newCounts[item.id] === undefined) {
          newCounts[item.id] = 0;
        }
      });

      setCounts(newCounts);
    }
  }, [selectedValues, items]);

  const handleCountChange = (itemId, count) => {
    const newCounts = {
      ...counts,
      [itemId]: count,
    };

    // Calculate the count for this category
    const categoryCount = Object.values(newCounts).reduce((acc, count) => (acc += count || 0), 0);

    // Check category limit only if hasLimitPerCategory is true
    if (attribute.hasLimitPerCategory) {
      const categoryLimit = attribute.limitPerCategory || Infinity;

      if (categoryCount > categoryLimit) {
        return;
      }
    }

    // Calculate new total count across all attributes
    const currentCategoryTotal = Object.values(counts).reduce(
      (acc, count) => acc + (count || 0),
      0,
    );
    const totalCount = totalSelectedCount - currentCategoryTotal + categoryCount;

    // Check total item limit
    const totalLimit = attribute.numberAllowedTotalItem || Infinity;

    if (totalCount > totalLimit) {
      return;
    }

    setCounts(newCounts);
    if (onChange) onChange(newCounts, itemId);
  };

  return (
    <div className="w-full space-y-2">
      {items.map((item) => {
        return (
          <>
            <div
              key={item.id}
              className={`flex items-center justify-between w-full py-[6px] px-3 rounded-[50px] hover:!border-primary ${
                counts[item.id] > 0
                  ? 'bg-secondary border border-secondary text-white'
                  : 'bg-white border border-[#f4f4f4] text-gray-900'
              }`}
            >
              <div className="flex-1">
                <h3 className="text-xs font-semibold">{item.label}</h3>
              </div>
              {item.price !== undefined && (
                <div className="text-xs font-semibold mr-4">
                  {item.currency || currency} {item.price}
                </div>
              )}
              <Counter
                className={`p-2 b rounded-[50px] ${
                  counts[item.id] > 0 ? 'bg-white' : '!bg-[#f4f4f4]'
                }`}
                initialValue={counts[item.id]}
                value={counts[item.id]}
                variant="default"
                onChange={(count) => handleCountChange(item.id, count)}
                maxCount={item.limit || Infinity}
              />
            </div>

            {connectedAttributes
              ?.filter((connectedAttr) =>
                item?.isconnectedto?.some((item2) => item2 === connectedAttr._id),
              )
              .map((connectedAttr) => {
                const connectedAttrSelection = selectedAttributes.find(
                  (attr) => attr.attributeId === connectedAttr._id,
                );

                const connectedSelectedOptions =
                  compact(connectedAttrSelection?.options)?.map((item3) => ({
                    ...item3,
                    id: item3._id,
                  })) || [];
                const isRequired = connectedAttr.attributesRequired;

                return (
                  <div key={connectedAttr._id} className="mt-2 p-3 bg-gray-100 rounded-md">
                    <h3 className="font-medium mb-2">
                      {connectedAttr.name}
                      {isRequired && (
                        <span className="text-sm text-red-500 ml-1">
                          &#40;{translations[language]?.restaurant_menu_required}&#41;
                        </span>
                      )}
                    </h3>
                    <RadioGroup
                      options={connectedAttr.options.map((item3) => ({
                        ...item3,
                        id: item3._id,
                        price: item3.Price || item3.price || 0,
                      }))}
                      onChange={(selectedOption) => {
                        onConnectedAttributeChange?.(
                          connectedAttr._id,
                          selectedOption,
                          selectedOption?.menuid || '',
                        );
                      }}
                      currency={currency}
                      required={connectedAttr.attributesRequired}
                      selectedOption={
                        connectedSelectedOptions.length > 0 ? connectedSelectedOptions[0] : null
                      }
                    />
                  </div>
                );
              })}
          </>
        );
      })}
    </div>
  );
};
